// **************** react import *******************
import React, { useState, useEffect, useCallback } from "react";

// ******************** Components  *******************

import CommonHeader from "../../../components/common-header";
import Table from "../../../components/table/table";
import { ConfirmPopup } from "../../../components/model-popup/confirmation-popup";
import { CustomButton } from "../../../components/buttons/button";

// ********************* utils function ***************
import { ChildProps } from "../../../utils/types/interFace";
import { debounce } from "../../../utils/redirections";

// ********************************* interface  ********************************
import { sortInterFace } from "../../../utils/types/interFace";

// ******************************** API & REDUX ***************
import { useDispatch, useSelector } from "react-redux";


// ********************* icons ***************
import EditIcon from "../../../assets/icons/edit.png";
import DeleteIcon from "../../../assets/icons/trash.png";

import { toast } from "react-toastify";
// ********************* edit popup ***************
import { EFaxNumberPopup } from "./efax-popup";
import { FetchEfaxList } from "../../../redux/slices/efax.slice";
import { DeleteEfaxNumber, DeleteMultipleEfax } from "../../../redux/slices/efax.slice";
import { update } from "lodash";

const EfaxMapping = ({ filteredAccess }: ChildProps) => {
  const dispatch: any = useDispatch();
  const userAccess = filteredAccess?.[0] || {};

  // Get efax list from Redux store
  const efaxList = useSelector((state: any) => state.efax || []);

  const [loader, setLoader] = useState<boolean>(true);

  // search
  const [showSearch, setShowSearch] = useState("");
  const [search, setSearch] = useState("");

  const handleSearch = useCallback(
    debounce((val: string) => {
      setSearch((prev) => (prev !== val ? val : prev));
    }, 500),
    []
  );

  // sort & pagination
  const [sort, setSort] = useState<sortInterFace>({ updated_at: "desc" });
  const [page, setPage] = useState<number>(1);
  const [limit] = useState(15);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [totalRecord, setTotalRecord] = useState(0);

  // modal & actions
  const [ViewDetails, setViewDetails] = useState<any>({});
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModel, setShowDeleteModel] = useState<boolean>(false);
  const [modelTitle, setModelTitle] = useState("");
  const [buttonLoader, setButtonLoader] = useState(false);

  const [facList, setFacList] = useState([])
  const [filterStatus,] = useState("");
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [multiDelete, setMultiDelete] = useState<any>(false);

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const handleEfaxList = (
    load: boolean,
    sorting: any = {},
    pageNumber?: number
  ) => {
    pageNumber && setPage(pageNumber);
    if (pageNumber || page === 1) {
      setLoader(load);
    }
    dispatch(
      FetchEfaxList({
        page: pageNumber || page,
        limit,
        status: filterStatus,
        sort_by:
          Object.keys(sorting).length !== 0
            ? Object.keys(sorting)[0]
            : "updated_at",
        sort_dir:
          Object.keys(sorting).length !== 0
            ? sorting[Object.keys(sorting)[0]]
            : "desc",
        search_value: search,
      })
    ).then((res: any) => {
      setTotalPage(res?.payload?.result?.total_pages);
      setTotalRecord(res?.payload?.result?.total_count);
      setLoader(false);
    });
  };

  const afterChangeEfaxList = () => {
    if (page === 1) {
      handleEfaxList(true, sort);
      setViewDetails({});
    } else {
      setPage(1);
    }
  };
  const handleMultiDelete = async () => {
    setButtonLoader(true);

    try {
      const selectedIds = selectedRows.map((row: any) => row?.id);
      const payload = { efax_ids: selectedIds };
      const res: any = await dispatch(
        DeleteMultipleEfax(payload)
      ).unwrap();
      toast.success(res?.message || "eFaxes deleted successfully");

      setShowDeleteModel(false);
      setViewDetails({});
      setMultiDelete(false);
      setSelectedRows([]);

      if (page === 1) {
        handleEfaxList(true, sort);
      } else {
        setPage(1);
      }
    } catch (err: any) {
      toast.error(
        err?.message ||
        err?.error ||
        err?.response?.data?.message ||
        "eFaxes delete failed"
      );
    } finally {
      setButtonLoader(false);
    }
  };


  const handleDelete = async () => {
    if (!ViewDetails?.id) return;

    setButtonLoader(true);

    try {
      const res = await dispatch(
        DeleteEfaxNumber(ViewDetails.id)
      ).unwrap();

      toast.success(res?.message || "eFax deleted successfully");

      setShowDeleteModel(false);
      setViewDetails({});
      afterChangeEfaxList(); // refresh table
    } catch (err: any) {
      toast.error(
        err?.message ||
        err?.error ||
        err?.response?.data?.message ||
        "Failed to delete eFax"
      );
    } finally {
      setButtonLoader(false);
    }
  };



  useEffect(() => handleSearch(showSearch), [showSearch]);
  useEffect(() => setPage(1), [search]);
  useEffect(() => handleEfaxList(true, sort), [page, sort, search, filterStatus]);


  return (
    <div className="w-full p-[10px] pl-[2px] pr-[2px]">
      <div className="pt-[16px]  pl-[2px] pr-[2px] bg-white rounded-[12px]">
        <CommonHeader
          heading="eFax Mapping"
          description="eFax fallback workflow : PCC access not available."
          addNew={userAccess.write_access}
          onclickAddButton={() => {
            setShowModal(true);
            setModelTitle("Add Efax Number");
          }}
          totalCount={totalRecord}
          countForText="Results"
          setShowSearch={setShowSearch}
          showSearch={showSearch}
          search
        />

        <Table
          loading={loader}
          data={efaxList}
          showCheckbox={userAccess.delete_access}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          MultiDeletedFucn={() => {
            setMultiDelete(true);
            setTimeout(() => {
              setShowDeleteModel(true);
            }, 10);
          }}
          tableScroll={"100%"}
          sort={true}
          hasNextPage={page < totalPage}
          NextPageData={handleNextPage}
          columns={[
            "Efax Number",
            "Location",
            "Updated By",
            ...(userAccess.delete_access || userAccess.update_access
              ? ["Action"]
              : []),
          ]}
          sortTitleKeys={{
            "Efax Number": "efax_number",
            "Updated By": "updated_by_last_name",
          }}
          handleSortfunc={(key: any, value: any) => {
            setSort({ [key]: value });
            page === 1
              ? handleEfaxList(false, { [key]: value })
              : handleEfaxList(false, { [key]: value }, 1);
          }}
          scopedSlots={{
            "Efax Number": ({ row }: any) => (
              <td title={row?.efax_number}>{row?.efax_number}</td>
            ),
            Location: ({ row }: any) => (
              <td title={row.locations?.map((loc: any) => loc.source_location).join(", ") || ""} >
                <div className="max-w-[400px] truncate">
                  {row.locations?.map((loc: any) => loc.source_location).join(", ") || ""}
                </div>
              </td>
            ),
            "Updated By": ({ row }: any) => (
              <td title={`${row.updated_by_last_name || ""}, ${row.updated_by_first_name || ""}`.trim()}>
                {`${row.updated_by_last_name || ""}, ${row.updated_by_first_name || ""}`.trim()}
              </td>
            ),
            Action: ({ row }: any) => (
              <td onClick={(e) => e.stopPropagation()}>
                <div className="flex gap-[16px] items-center justify-center pr-[4px]">
                  {userAccess.update_access && (
                    <img
                      src={EditIcon}
                      alt="Edit"
                      className="cursor-pointer w-[18px] h-[18px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewDetails(row);
                        setModelTitle("Update Efax Number");
                        setShowModal(true);
                      }}
                    />
                  )}
                  {userAccess.delete_access && (
                    <img
                      src={DeleteIcon}
                      alt="Delete"
                      className="cursor-pointer w-[18px] h-[18px]"
                      onClick={() => {
                        setViewDetails(row);
                        setShowDeleteModel(true);
                      }}
                    />
                  )}
                </div>
              </td>
            ),
          }}
        />
        {/* *************************** editing modal *******************/}
        {showModal ? (
          <EFaxNumberPopup
            showModal={showModal}
            setShowModal={setShowModal}
            afterChangeEfaxList={afterChangeEfaxList}
            handleClose={() => {
              setViewDetails({});
              setShowModal(false);
            }}
            title={modelTitle}
            setTitle={setModelTitle}
            popupDetails={ViewDetails}
            facList={facList}

          />
        ) : null}


        {/* Delete confirmation */}
        <ConfirmPopup showModal={showDeleteModel} icon>
          <div className="text-[16px] font-semibold text-center text-[#061018] line-height-[24px]">
            Delete Efax Number?
          </div>
          <div className="text-[12px] font-normal text-center text-[#061018] mt-[8px] mb-[16px] ">
            You are about to delete{" "}
            <span className="font-semibold">
              {`"${ViewDetails?.efax_number}"`}
            </span> {" "}

          </div>
          <div className="flex justify-center gap-[16px] items-center w-full">
            <CustomButton
              buttonName="No, Keep it"
              primary={false}
              buttonStyle="w-full text-[#1C252D] text-[14px] p-[12px] text-[14px] font-semibold leading-[17px]   "
              disable={buttonLoader}
              onClick={() => {
                setShowDeleteModel(false);
                setViewDetails({});
                setMultiDelete(false);
              }}
            />
            <CustomButton
              buttonName="Yes, Remove !"
              buttonStyle="w-full text-white bg-[#D41E1E] 
              hover:bg-[#B51C1C] text-[14px] p-[12px] 
              text-[14px] font-semibold leading-[17px]"
              loader={buttonLoader}
              disable={buttonLoader}
              loaderStyle="w-full bg-[#D41E1E]/80  py-[13px] "
              onClick={() => {
                multiDelete ? handleMultiDelete() : handleDelete();
              }}
            />
          </div>
        </ConfirmPopup>
      </div>
    </div>
  );
};

export default EfaxMapping;